import type { PayOut, PspType } from '@core/types/generated'
import type { GQLTransferComparison } from '../generated/transfer-comparison'
import type { Tab, Filters, HistoricalData, ProviderQuote } from '../types'
import { processQuotes } from './process-quotes'
import type { PaymentType } from '@core/types/imt'

type TabConfig = {
  type: PaymentType
  payouts?: {
    include?: PayOut[]
    exclude?: PayOut[]
  }
  providerTypes?: {
    include?: PspType[]
    exclude?: PspType[]
  }
  order: (countryTo: string, amountUsd: number) => number | undefined
}

const CASH_THRESHOLD = 5000
const WALLET_THRESHOLD = 5000
const BROKER_THRESHOLD = 50000

export const TABS_CONFIG: readonly TabConfig[] = [
  {
    type: 'bank',
    payouts: { include: ['BANK', 'INTERNATIONAL_BANK'] },
    providerTypes: { exclude: ['FOREIGN_EXCHANGE_COMPANY'] },
    order: (countryTo) => getOrder('bank', countryTo),
  },
  {
    type: 'cash',
    payouts: { include: ['CASH'] },
    providerTypes: { exclude: ['FOREIGN_EXCHANGE_COMPANY'] },
    order: (countryTo, amountUsd) =>
      amountUsd < CASH_THRESHOLD ? getOrder('cash', countryTo) : undefined,
  },
  {
    type: 'delivery',
    payouts: { include: ['DELIVERY'] },
    providerTypes: { exclude: ['FOREIGN_EXCHANGE_COMPANY'] },
    order: (countryTo) => getOrder('delivery', countryTo),
  },
  {
    type: 'wallet',
    payouts: { include: ['WALLET'] },
    providerTypes: { exclude: ['FOREIGN_EXCHANGE_COMPANY'] },
    order: (countryTo, amountUsd) =>
      amountUsd < WALLET_THRESHOLD ? getOrder('wallet', countryTo) : undefined,
  },
  {
    type: 'card',
    payouts: { include: ['CARD'] },
    providerTypes: { exclude: ['FOREIGN_EXCHANGE_COMPANY'] },
    order: (countryTo) => getOrder('card', countryTo),
  },
  {
    type: 'topup',
    payouts: { include: ['TOPUP'] },
    providerTypes: { exclude: ['FOREIGN_EXCHANGE_COMPANY'] },
    order: (countryTo) => getOrder('topup', countryTo),
  },
  {
    type: 'broker',
    providerTypes: { include: ['FOREIGN_EXCHANGE_COMPANY'] },
    order: (countryTo, amountUsd) =>
      amountUsd > BROKER_THRESHOLD ? 0 : getOrder('broker', countryTo),
  },
]

function getOrder(type: PaymentType, countryTo: string) {
  const index = TAB_ORDER_BY_COUNTRY[countryTo]?.indexOf(type) ?? -1
  return index === -1 ? undefined : index
}

// Custom tab order for some receiving countries
const TAB_ORDER_BY_COUNTRY: Record<string, PaymentType[] | undefined> = {
  TN: ['cash'],
  MA: ['cash'],
  MX: ['cash'],
  CI: ['cash'],
  GA: ['cash'],
  SA: ['cash'],
  BJ: ['cash'],
  PH: ['cash'],
  AR: ['cash'],
  NG: ['cash'],
  AM: ['cash'],
  BI: ['cash'],
  CM: ['cash'],
  CD: ['cash'],
  DO: ['cash'],
  FJ: ['cash'],
  GT: ['cash'],
  GN: ['cash'],
  PY: ['cash'],
  RW: ['cash'],
  ZW: ['cash'],
  NP: ['cash'],
  BO: ['cash'],
  MG: ['cash'],
  GM: ['cash'],
  JM: ['cash'],
  NI: ['cash'],
  EC: ['cash'],
  EG: ['cash'],
  TZ: ['wallet', 'cash'],
  KE: ['wallet', 'cash'],
  UG: ['wallet', 'cash'],
  GH: ['wallet', 'cash'],
  UA: ['card'],
  GE: ['card'],
  NA: ['card'],
  MD: ['card'],
  DZ: ['card', 'cash'],
}

function getConfig(amountUsd: number, countryTo: string) {
  const config = TABS_CONFIG.slice()

  const order = (config: TabConfig) =>
    config.order(countryTo, amountUsd) ?? Number.POSITIVE_INFINITY
  config.sort((a, b) => order(a) - order(b))

  return config
}

function getFilter(tabConfig: TabConfig | undefined) {
  if (!tabConfig) {
    return undefined
  }
  return ({ psp, quotes }: ProviderQuote) => {
    if (tabConfig.providerTypes) {
      if (tabConfig.providerTypes.exclude?.includes(psp.type)) {
        return []
      }
      if (tabConfig.providerTypes.include && !tabConfig.providerTypes.include.includes(psp.type)) {
        return []
      }
    }
    if (tabConfig.payouts) {
      return quotes.filter((quote) => {
        if (tabConfig.payouts?.exclude?.includes(quote.payout)) {
          return false
        }
        if (tabConfig.payouts?.include && !tabConfig.payouts.include.includes(quote.payout)) {
          return false
        }
        return true
      })
    }
    return quotes
  }
}

export function getBestDealIndexes(tabs: Tab[]) {
  const bestDealAmount = tabs.reduce((prev, current) =>
    prev.amount > current.amount ? prev : current,
  ).amount
  const indexes = tabs.reduce((acc: number[], curr, index) => {
    if (curr.amount === bestDealAmount) {
      acc.push(index)
    }
    return acc
  }, [])
  if (indexes.length === tabs.length) {
    return []
  }
  return indexes
}

export function computeTabs(
  results: GQLTransferComparison['results'],
  countryTo: string,
  filters: Filters,
  historicalData?: HistoricalData,
) {
  const config = getConfig(results.corridor.amountUSD, countryTo)
  const tabs = config
    .map((tabConfig) => {
      const items = processQuotes(
        results.providerQuotes,
        { ...filters, tab: getFilter(tabConfig) },
        getCheapestOver3Months(historicalData, tabConfig.type),
      )
      if (!items.length) {
        return null
      }

      const cheapest = items.find((i) => i.bestDeal)
      return {
        type: tabConfig.type,
        amount: cheapest?.cheapestService.amount ?? 0,
        items,
      }
    })
    .filter((tab): tab is Tab => !!tab)

  if (!tabs.length) {
    return []
  }

  const bestDealIndexes = getBestDealIndexes(tabs)
  if (bestDealIndexes.length) {
    bestDealIndexes.forEach((index) => {
      tabs[index].label = 'best_deal'
    })
    if (!tabs[0].label) {
      tabs[0].label = 'popular'
    }
  }

  return tabs
}

export function getTabFilters(type: PaymentType) {
  const config = TABS_CONFIG.find((c) => c.type === type)
  return {
    payouts: config?.payouts,
    providerTypes: config?.providerTypes,
  }
}

function getCheapestOver3Months(historicalData: HistoricalData | undefined, tabType: PaymentType) {
  if (!historicalData) {
    return null
  }
  switch (tabType) {
    case 'bank':
      return historicalData.cheapestToBank
    case 'cash':
      return historicalData.cheapestToCash
    case 'card':
      return historicalData.cheapestToCard
    case 'wallet':
      return historicalData.cheapestToWallet
    default:
      return null
  }
}
